import React from 'react';
import { Image } from 'lucide-react';
import ColorEditor from '../ColorEditor';

const BannerEditor = ({ content, onContentChange, uploading, onImageUpload, onOpenImagePicker, site, onCreateCustomTheme }) => {
  return (
    <div className="space-y-2">
      {/* Text Content */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Banner text</label>
        <input
          type="text"
          value={content.text || ''}
          onChange={(e) => onContentChange('text', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          placeholder="Welcome to our business"
        />
      </div>

      {/* Banner Height */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Banner height</label>
        <div className="grid grid-cols-2 gap-1">
          {[
            { name: 'small', label: 'Small (200px)' },
            { name: 'medium', label: 'Medium (400px)' },
            { name: 'large', label: 'Large (600px)' },
            { name: 'full', label: 'Full screen' }
          ].map((height) => (
            <button
              key={height.name}
              onClick={() => onContentChange('height', height.name)}
              className={`px-2 py-1 text-xs border rounded ${
                (content.height || 'medium') === height.name ? 'bg-gray-200 border-gray-400' : 'border-gray-300'
              }`}
            >
              {height.label}
            </button>
          ))}
        </div>
      </div>

      {/* Text Size */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Text size</label>
        <div className="flex items-center gap-1">
          <span className="text-xs">A</span>
          <input
            type="range"
            min="1"
            max="7"
            value={content.textSize || 4}
            onChange={(e) => onContentChange('textSize', parseInt(e.target.value))}
            className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs">A</span>
          <span className="text-xs text-gray-500 ml-2">Size {content.textSize || 4}</span>
        </div>
      </div>

      {/* Text Color */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Text color</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={content.textColor || '#ffffff'}
            onChange={(e) => onContentChange('textColor', e.target.value)}
            className="w-8 h-6 border border-gray-300 rounded cursor-pointer"
          />
          <span className="text-xs text-gray-500">
            {content.textColor || '#ffffff'}
          </span>
        </div>
      </div>

      {/* Text Shadow */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-gray-900">Text shadow</label>
          <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
            <input 
              id="textShadowToggle"
              type="checkbox" 
              checked={content.textShadow || false}
              onChange={(e) => onContentChange('textShadow', e.target.checked)}
              className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
            />
            <label htmlFor="textShadowToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
          </div>
        </div>
      </div>

      {/* Background Image */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-gray-900">Background image</label>
          <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
            <input 
              type="checkbox" 
              checked={content.backgroundImage || false}
              onChange={(e) => onContentChange('backgroundImage', e.target.checked)}
              className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
            />
            <label className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
          </div>
        </div>
        {content.backgroundImage && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <div 
                className="flex-1 h-20 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors relative overflow-hidden"
                onClick={() => document.getElementById('bannerBackgroundImageInput').click()}
              >
                {content.backgroundImageUrl ? (
                  <img 
                    src={content.backgroundImageUrl} 
                    alt="Background" 
                    className="w-full h-full object-cover rounded"
                  />
                ) : (
                  <div className="text-center">
                    <Image className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                    <div className="text-xs text-gray-500">
                      {uploading ? 'Uploading...' : 'Upload'}
                    </div>
                  </div>
                )}
                <input
                  id="bannerBackgroundImageInput"
                  type="file"
                  accept="image/*"
                  onChange={(e) => onImageUpload(e.target.files[0], 'backgroundImageUrl')}
                  className="hidden"
                  disabled={uploading}
                />
              </div>
              <button
                onClick={() => onOpenImagePicker('backgroundImageUrl')}
                className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition min-w-0 flex flex-col items-center justify-center"
                disabled={uploading}
              >
                <Image className="w-3 h-3 mb-1" />
                <span>Unsplash</span>
              </button>
            </div>
            {content.backgroundImageUrlCredit && (
              <div className="text-xs text-gray-500">
                Photo by{' '}
                <a 
                  href={content.backgroundImageUrlCredit.photographerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {content.backgroundImageUrlCredit.photographer}
                </a>
                {' '}on Unsplash
              </div>
            )}
          </div>
        )}
      </div>

      {/* Overlay Color */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Overlay color</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={content.overlayColor || '#000000'}
            onChange={(e) => onContentChange('overlayColor', e.target.value)}
            className="w-8 h-6 border border-gray-300 rounded cursor-pointer"
          />
          <span className="text-xs text-gray-500">
            {content.overlayColor || '#000000'}
          </span>
        </div>
      </div>

      {/* Overlay Opacity */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Overlay opacity</label>
        <div className="flex items-center gap-1">
          <span className="text-xs">○</span>
          <input
            type="range"
            min="0"
            max="100"
            value={content.overlayOpacity || 50}
            onChange={(e) => onContentChange('overlayOpacity', parseInt(e.target.value))}
            className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs">●</span>
          <span className="text-xs text-gray-500 ml-2">{content.overlayOpacity || 50}%</span>
        </div>
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default BannerEditor;